import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'libs/styled';
import { useTranslation } from 'react-i18next';
import { ProductCard, type ProductCardProduct } from 'components/product/ProductCard';
import { isWideProduct } from 'Utils/isWideProduct';
import { GRID_GAP_PX } from 'config/branding/grid';
import { columnWidthPx, imageHeightFromSpanWidthPx, spanWidthPx } from 'Utils/gridLayoutPx';

const MOBILE_GRID_COLS = 12;
const NARROW_SPAN_COLS = 9;
const WIDE_SPAN_COLS = 18;

/** Space below cards so the horizontal scrollbar does not overlap product text. */
const SCROLLBAR_OFFSET_BOTTOM_PX = 10;

/** Inside scroll clip (`overflow-y: hidden`); keeps link focus outline from being cut off at the top. */
const FOCUS_OUTLINE_PADDING_TOP_PX = 6;

/** Matches ProductCard title + designer: 15px / 120% line-height (typography tokens), three lines reserved. */
const PRODUCT_CARD_INFO_MIN_HEIGHT = 'calc(3 * 15px * 1.2)';

const SliderContentWrapper = styled('div')`
    padding-left: var(--grid-padding, 10px);
    padding-right: var(--grid-padding, 10px);
`;

const SliderBleedWrapper = styled('div')`
    grid-column: 1 / -1;
    margin-left: calc(-1 * var(--grid-padding, 10px));
    margin-right: calc(-1 * var(--grid-padding, 10px));
    width: calc(100% + 2 * var(--grid-padding, 10px));
`;

const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const scrollViewportForwardProps = {
    shouldForwardProp: (prop: string) => !prop.startsWith('$'),
};

const ScrollViewport = styled('div', scrollViewportForwardProps)<{ $scrollbarRevealed: boolean }>`
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-top: ${FOCUS_OUTLINE_PADDING_TOP_PX}px;
    padding-bottom: ${SCROLLBAR_OFFSET_BOTTOM_PX}px;

    ${({ $scrollbarRevealed }) =>
        $scrollbarRevealed
            ? ''
            : `
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
            height: 0;
        }
    `}
`;

const FlexRow = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--grid-gap);
    padding: 0 var(--grid-gap);
    width: max-content;
    min-height: min-content;
`;

const CardCell = styled('div', filterTransientProps)<{ $widthPx: number }>`
    flex: 0 0 ${({ $widthPx }) => $widthPx}px;
    min-width: 0;
`;

const CardInner = styled('div', filterTransientProps)<{ $imageHeightPx: number | null }>`
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    container-type: inline-size;
    --product-image-height: ${({ $imageHeightPx }) =>
        $imageHeightPx != null ? `${$imageHeightPx}px` : 'calc(100cqw * 5 / 4)'};
`;

export interface ProductScrollMobileProps {
    products: ProductCardProduct[];
}

const spanColsForProduct = (product: ProductCardProduct): number =>
    isWideProduct(product) ? WIDE_SPAN_COLS : NARROW_SPAN_COLS;

/** Native horizontal product row (below desktop product-slider breakpoint). */
const ProductScrollMobile: React.FC<ProductScrollMobileProps> = ({ products }) => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [viewportWidthPx, setViewportWidthPx] = useState(0);
    /** Hidden until first scroll/focus; we do not re-hide when the product list changes (pageload-only clean look). */
    const [scrollbarRevealed, setScrollbarRevealed] = useState(false);

    const productIdsKey = useMemo(() => products.map((product) => product.objectID).join(','), [products]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setViewportWidthPx(entry.contentRect.width);
            }
        });
        resizeObserver.observe(node);
        setViewportWidthPx(node.getBoundingClientRect().width);
        return () => resizeObserver.disconnect();
    }, [products.length]);

    useEffect(() => {
        const node = scrollRef.current;
        if (node) {
            node.scrollLeft = 0;
        }
    }, [productIdsKey]);

    const revealScrollbar = useCallback(() => {
        setScrollbarRevealed(true);
    }, []);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node || scrollbarRevealed) {
            return undefined;
        }

        const listenerOptions: AddEventListenerOptions = { passive: true };
        node.addEventListener('scroll', revealScrollbar, listenerOptions);
        node.addEventListener('focusin', revealScrollbar, listenerOptions);

        return () => {
            node.removeEventListener('scroll', revealScrollbar);
            node.removeEventListener('focusin', revealScrollbar);
        };
    }, [scrollbarRevealed, revealScrollbar]);

    if (products.length === 0) {
        return null;
    }

    const gridInnerWidthPx = Math.max(0, viewportWidthPx - 2 * GRID_GAP_PX);
    const columnWidth = columnWidthPx(gridInnerWidthPx, GRID_GAP_PX, MOBILE_GRID_COLS);
    const narrowSpanWidthPx = columnWidth > 0 ? spanWidthPx(NARROW_SPAN_COLS, columnWidth, GRID_GAP_PX) : 0;
    const sharedImageHeightPx = narrowSpanWidthPx > 0 ? imageHeightFromSpanWidthPx(narrowSpanWidthPx) : null;

    const scrollAriaLabel = t('productSlider.scrollRegionLabel', {
        defaultValue: 'Products, scroll horizontally',
    });

    return (
        <SliderContentWrapper>
            <SliderBleedWrapper>
                <ScrollViewport
                    ref={scrollRef}
                    role="region"
                    aria-label={scrollAriaLabel}
                    $scrollbarRevealed={scrollbarRevealed}
                    // eslint-disable-next-line styled-components-a11y/no-noninteractive-tabindex -- scroll region needs focus for keyboard horizontal scroll
                    tabIndex={0}
                >
                    <FlexRow>
                        {products.map((product, index) => {
                            const spanCols = spanColsForProduct(product);
                            const widthPx = columnWidth > 0 ? spanWidthPx(spanCols, columnWidth, GRID_GAP_PX) : 200;
                            return (
                                <CardCell key={product.objectID || String(index)} $widthPx={widthPx}>
                                    <CardInner $imageHeightPx={sharedImageHeightPx}>
                                        <ProductCard
                                            product={product}
                                            gridColumn="1 / -1"
                                            productInfoMinHeight={PRODUCT_CARD_INFO_MIN_HEIGHT}
                                        />
                                    </CardInner>
                                </CardCell>
                            );
                        })}
                    </FlexRow>
                </ScrollViewport>
            </SliderBleedWrapper>
        </SliderContentWrapper>
    );
};

export default ProductScrollMobile;
